import React, {useContext, useEffect, useRef, useState} from 'react';
import {Box, Link} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../const/i18nNamespaces';
import {AlertContext} from '../../../contexts/AlertContext';
import accountProvider from '../../../services/accountProvider';
import axios from 'axios';
import { accountApi } from '../../../api/account/accountApi';
import { parseVerifyCodeError } from '../../../utils/error';

const TIME_TO_NEXT_CODE_SEND = 30000;

type SendCodeAgainProps = {
    mt?: number | string;
    mb?: number | string;
    onCallWithCode?: () => void;
};
const SendCodeAgain = ({mt, mb, onCallWithCode}: SendCodeAgainProps) => {
    const {t} = useTranslation(i18nNamespaces.AUTH);
    const {t: errorT} = useTranslation(i18nNamespaces.ERRORS);
    const [remainingTime, setRemainingTime] = useState<number | null>(null);
    const [smsAttemptsCount, setSmsAttemptsCount] = useState(0);
    const [callAttemptsCount, setCallAttemptsCount] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const openAlert = useContext(AlertContext);
    const isCallWithCodeUnlocked = smsAttemptsCount >= 2 && remainingTime === 0 && callAttemptsCount === 0;

    useEffect(() => {
        if (remainingTime === null) return;
        if (remainingTime <= 0) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            setRemainingTime(0);
        }
    }, [remainingTime]);

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const startCountdown = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        setRemainingTime(TIME_TO_NEXT_CODE_SEND);
        intervalRef.current = setInterval(() => {
            setRemainingTime(prev => (prev !== null ? prev - 1000 : 0));
        }, 1000);
    };

     const handleVerifyCodeError = (err: unknown) => {
        if (axios.isAxiosError(err)){
            if(err?.response?.status === 401){
                openAlert({
                    severity: 'error',
                    message: errorT("sessionExpired"),
                });
            }
            const error = err.response?.data as string[];
            openAlert({
                severity: 'error',
                message: parseVerifyCodeError(error, errorT),
            });
        }
    };

    const handleSendCodeAgain = async () => {
        try {
            startCountdown();
            await accountProvider.sendSMSCode();
        } catch (err) {
            console.error(err);
            handleVerifyCodeError(err);
        } finally {
            setSmsAttemptsCount(prev => prev + 1);
        }
    };

    const handleCallWithCode = async () => {
        try {
            await accountApi.callWithSmsCode();
            setCallAttemptsCount(prev => prev + 1);
            if (onCallWithCode) {
                onCallWithCode();
            }
        }catch(err){
            console.error('Call with code error', err);
            handleVerifyCodeError(err);
        }
    }

    if(callAttemptsCount > 0){
        return null;
    }

    return (
        <Box mt={mt} mb={mb}>
            {t('codeNotReceived')}{' '}
            {remainingTime > 0 ? (
                t('resendIn', {seconds: remainingTime / 1000})
            ) : (
                <Link
                    color={'secondary'}
                    style={{cursor: 'pointer'}}
                    onClick={isCallWithCodeUnlocked ? handleCallWithCode : handleSendCodeAgain}
                >
                    {isCallWithCodeUnlocked ? t('callWithCode') : t('sendAgain')}
                </Link>
            )}
        </Box>
    );
};

export default SendCodeAgain;
